import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi, ItemsParams, paymentMethodsApi, sessionApi } from "./api";
import { CollectRequestTarget } from "./types";
import { ApiAuth, ApiError } from "../../../lib/api";
import { createPaymentAttempt, outcomeOfError } from "./paymentAttempt";

const ITEMS_KEY = "patient-billing-items";
const CONFIG_KEY = "patient-billing-payment-config";
const BOOTSTRAP_KEY = "patient-session-bootstrap";
const METHODS_KEY = "patient-payment-methods";
const DOCS_KEY = "patient-billing-documents";

export function useBillingItems(auth: ApiAuth, params: ItemsParams) {
  return useQuery({
    queryKey: [ITEMS_KEY, params],
    queryFn: () => billingApi.listItems(auth, params),
  });
}

export function useItemDocuments(
  auth: ApiAuth,
  itemId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: [DOCS_KEY, itemId],
    queryFn: () => billingApi.listDocuments(auth, itemId!),
    enabled: enabled && !!itemId,
  });
}

export function useInvalidateBillingItems() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [ITEMS_KEY] });
}

export function usePaymentConfig(auth: ApiAuth, locationId: string | null) {
  return useQuery({
    queryKey: [CONFIG_KEY, locationId],
    queryFn: () => billingApi.paymentConfig(auth, locationId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useSessionBootstrap(auth: ApiAuth, enabled: boolean) {
  return useQuery({
    queryKey: [BOOTSTRAP_KEY],
    queryFn: () => sessionApi.bootstrap(auth),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// `dependentPatientId` names whose charges are being paid. Absent, the payer
// and the subject are the same person and the canonical route derives both from
// the session.
//
// Each "Pay" is an attempt with its own key, kept across retries of the same
// selection until the attempt has a definitive answer (paymentAttempt.ts).
export function useCollect(auth: ApiAuth, dependentPatientId?: string | null) {
  const qc = useQueryClient();
  const attempt = useRef(createPaymentAttempt());
  return useMutation({
    mutationFn: (body: {
      targets: CollectRequestTarget[];
      locationId?: string | null;
      paymentMethodId?: string | null;
      returnUrl?: string | null;
    }) => {
      const key = attempt.current.keyFor({ ...body, dependentPatientId: dependentPatientId ?? null });
      return billingApi.collect(auth, body, dependentPatientId, key);
    },
    onSuccess: () => {
      attempt.current.settle("success");
      qc.invalidateQueries({ queryKey: [ITEMS_KEY] });
    },
    onError: (error) => {
      attempt.current.settle(outcomeOfError(error as ApiError));
    },
  });
}

export function usePaymentMethods(auth: ApiAuth, enabled = true) {
  return useQuery({
    queryKey: [METHODS_KEY],
    queryFn: () => paymentMethodsApi.list(auth),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateSetupIntent(auth: ApiAuth) {
  return useMutation({
    mutationFn: () => paymentMethodsApi.createSetupIntent(auth),
  });
}

export function useSetDefaultPaymentMethod(auth: ApiAuth) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      paymentMethodsApi.setDefault(auth, paymentMethodId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [METHODS_KEY] });
    },
  });
}

export function useSetCardNickname(auth: ApiAuth) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentMethodId,
      nickname,
    }: {
      paymentMethodId: string;
      nickname: string | null;
    }) => paymentMethodsApi.setNickname(auth, paymentMethodId, nickname),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [METHODS_KEY] });
    },
  });
}

export function useDetachPaymentMethod(auth: ApiAuth) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      paymentMethodsApi.detach(auth, paymentMethodId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [METHODS_KEY] });
    },
  });
}

export function useInvalidatePaymentMethods() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [METHODS_KEY] });
}
